import { Col, Form, FormInstance, Radio, Row, Typography } from 'antd';
import { useIntl } from 'react-intl';
import {
  CheckboxGroupButtons,
  FloatingFormItem,
  InputDecimal,
} from '@/components/_common';
import ProductiveHoursSoldReportTypeSwitch from './ProductiveHoursSoldReportTypeSwitch';
import ProductiveHoursSoldMoreOptions from './ProductiveHoursSoldMoreOptions';
import ReportSelectMonthFields from '@/components/_common/ReportSelectMonthFields';
import {
  DateFilterByTypes,
  HoursSoldReportType,
} from '@/core/domain/NetReport/WorkOrder/WorkOrder.types';
import { useDefaultEmptyFieldError } from '@/hooks';

interface Props {
  form: FormInstance;
}

export default function ProductiveHoursSoldForm({ form }: Props) {
  const intl = useIntl().formatMessage;
  const { Title } = Typography;

  const defaultEmptyFieldError = useDefaultEmptyFieldError();

  const filterDateOption = [
    {
      value: DateFilterByTypes.entry,
      label: intl({ id: 'productiveHoursSold.option.input' }),
    },
    {
      value: DateFilterByTypes.authorization,
      label: intl({ id: 'productiveHoursSold.option.authorization' }),
    },
    {
      value: DateFilterByTypes.closing,
      label: intl({ id: 'productiveHoursSold.option.closure' }),
    },
    {
      value: DateFilterByTypes.leave,
      label: intl({ id: 'productiveHoursSold.option.exit' }),
    },
  ];

  const situationOption = [
    { value: 'open', label: intl({ id: 'productiveHoursSold.option.open' }) },
    {
      value: 'authorized',
      label: intl({ id: 'productiveHoursSold.option.authorized' }),
    },
    {
      value: 'closed',
      label: intl({ id: 'productiveHoursSold.option.closed' }),
    },
    {
      value: 'canceled',
      label: intl({ id: 'productiveHoursSold.option.canceled' }),
    },
  ];

  const reportType = [
    {
      value: HoursSoldReportType.employee,
      label: intl({ id: 'productiveHoursSold.option.perEmployee' }),
    },
    {
      value: HoursSoldReportType.area,
      label: intl({ id: 'productiveHoursSold.option.byArea' }),
    },
  ];

  return (
    <Form layout="vertical" form={form}>
      <ReportSelectMonthFields
        form={form}
        dateFilter={
          <Form.Item
            style={{ margin: '4px 0' }}
            name="dateFilterBy"
            label={
              <Title level={5} style={{ margin: '0' }}>
                {intl({ id: 'productiveHoursSold.label.filterDate' })}
              </Title>
            }
            initialValue={DateFilterByTypes.authorization}
          >
            <Radio.Group
              buttonStyle="solid"
              optionType="button"
              size="large"
              options={filterDateOption}
            />
          </Form.Item>
        }
      />

      <Form.Item
        style={{ margin: '4px 0' }}
        name="situation"
        label={
          <Title level={5} style={{ margin: '0' }}>
            {intl({ id: 'productiveHoursSold.label.status' })}
          </Title>
        }
        initialValue={['authorized']}
      >
        <CheckboxGroupButtons options={situationOption} />
      </Form.Item>

      <Form.Item
        name="typeReport"
        label={
          <Title level={5} style={{ margin: '0' }}>
            {intl({ id: 'productiveHoursSold.label.reportType' })}
          </Title>
        }
        initialValue={HoursSoldReportType.employee}
      >
        <Radio.Group
          buttonStyle="solid"
          optionType="button"
          size="large"
          options={reportType}
        />
      </Form.Item>

      <Row gutter={16}>
        <Col span={6}>
          <FloatingFormItem
            label={intl({ id: 'productiveHoursSold.label.workingDays' })}
            name="workDays"
            rules={[defaultEmptyFieldError]}
          >
            <InputDecimal fractions={0} />
          </FloatingFormItem>
        </Col>

        <Col span={6}>
          <FloatingFormItem
            label={intl({ id: 'productiveHoursSold.label.hoursWorked' })}
            name="workHours"
            rules={[defaultEmptyFieldError]}
          >
            <InputDecimal fractions={0} />
          </FloatingFormItem>
        </Col>
      </Row>

      <ProductiveHoursSoldReportTypeSwitch />

      <ProductiveHoursSoldMoreOptions />
    </Form>
  );
}
